import { notificationApi } from "@/getinfo-api";
import { i18n } from "@/locales/settings";
import { LocalesFields } from "@/locales/translateInterface";
import { CompanySettings as ICompanySettings } from "@/api-v2/notification/models/company-settings";
import { UserSettings as IUserSettings } from "@/api-v2/notification/models/user-settings";
import { envVars } from "@/plugins/env-vars";

enum NotificationSettingsTypes {
  NewLearningRequest = "newLearningRequest",
  StudentStartLearning = "studentStartLearning",
  StudentFinishLearning = "studentFinishLearning"
}

const User = {
  getStudentStartLearningSettings: async (userId: string) => {
    const { data } = await notificationApi.User.getNotificationUserSettings(
      userId,
      NotificationSettingsTypes.StudentStartLearning
    );
    return data;
  },
  getStudentFinishLearningSettings: async (userId: string) => {
    const { data } = await notificationApi.User.getNotificationUserSettings(
      userId,
      NotificationSettingsTypes.StudentFinishLearning
    );
    return data;
  },
  getNewLearningRequestSettings: async (userId: string) => {
    const { data } = await notificationApi.User.getNotificationUserSettings(
      userId,
      NotificationSettingsTypes.NewLearningRequest
    );
    return data;
  },
  setStudentStartLearningSettings: async (
    userId: string,
    settings: IUserSettings
  ) => {
    await notificationApi.User.editNotificationUserSettings(
      userId,
      NotificationSettingsTypes.StudentStartLearning,
      settings
    );
  },
  setStudentFinishLearning: async (userId: string, settings: IUserSettings) => {
    await notificationApi.User.editNotificationUserSettings(
      userId,
      NotificationSettingsTypes.StudentFinishLearning,
      settings
    );
  },
  setNewLearningRequest: async (userId: string, settings: IUserSettings) => {
    await notificationApi.User.editNotificationUserSettings(
      userId,
      NotificationSettingsTypes.NewLearningRequest,
      settings
    );
  }
};

const Company = {
  getTokens: async (companyId: string) => {
    const { data } = await notificationApi.Company.getNotificationTokens(
      companyId
    );
    return data;
  },
  getStudentStartLearningSettings: async (companyId: string) => {
    const {
      data
    } = await notificationApi.Company.getNotificationCompanySettings(
      companyId,
      NotificationSettingsTypes.StudentStartLearning
    );
    return data;
  },
  getStudentFinishLearningSettings: async (companyId: string) => {
    const {
      data
    } = await notificationApi.Company.getNotificationCompanySettings(
      companyId,
      NotificationSettingsTypes.StudentFinishLearning
    );
    return data;
  },
  getNewLearningRequestSettings: async (companyId: string) => {
    const {
      data
    } = await notificationApi.Company.getNotificationCompanySettings(
      companyId,
      NotificationSettingsTypes.NewLearningRequest
    );
    return data;
  },
  setStudentStartLearningSettings: async (
    companyId: string,
    settings: ICompanySettings
  ) => {
    await notificationApi.Company.editNotificationCompanySettings(
      companyId,
      NotificationSettingsTypes.StudentStartLearning,
      settings
    );
  },
  setStudentFinishLearning: async (
    companyId: string,
    settings: ICompanySettings
  ) => {
    await notificationApi.Company.editNotificationCompanySettings(
      companyId,
      NotificationSettingsTypes.StudentFinishLearning,
      settings
    );
  },
  setNewLearningRequest: async (
    companyId: string,
    settings: ICompanySettings
  ) => {
    await notificationApi.Company.editNotificationCompanySettings(
      companyId,
      NotificationSettingsTypes.NewLearningRequest,
      settings
    );
  }
};

const translateNotificationType = (type: NotificationSettingsTypes) => {
  let value = "";
  if (type === NotificationSettingsTypes.NewLearningRequest)
    value = i18n.tc(LocalesFields.NewDeal);
  if (type === NotificationSettingsTypes.StudentStartLearning)
    value = i18n.tc(LocalesFields.EnrollmentInTheCourse);
  if (type === NotificationSettingsTypes.StudentFinishLearning)
    value = i18n.tc(LocalesFields.PassingTheTestSuccessfully);

  return value;
};

export const newsSettings: INewsSettings = {
  url: envVars.getStr("VUE_APP_NEWS_URL"),
  authInfo: `Basic ${window.btoa(envVars.getStr("VUE_APP_NEWS_AUTH"))}`
};

export const importantNotification: INewsSettings = {
  url: envVars.getStr("VUE_APP_IMPORTANT_NOTIFICATION_URL"),
  authInfo: `Basic ${window.btoa(envVars.getStr("VUE_APP_NEWS_AUTH"))}`
};

interface INewsSettings {
  url: string;
  authInfo: string;
}

export class News<T> {
  private url: string;
  private itemsList: Array<T> = [];

  private requestOptions: any = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: ""
    }
  };

  constructor(options: INewsSettings) {
    this.url = options.url;
    this.requestOptions.headers["Authorization"] = options.authInfo;
  }

  async getNews() {
    try {
      const response = await fetch(this.url, this.requestOptions);
      this.itemsList = await response?.json();
      return this.itemsList;
    } catch (e) {
      throw Error(e);
    }
  }

  get items() {
    return this.itemsList;
  }
}

export { Company, User, NotificationSettingsTypes, translateNotificationType };
